import { useState, useEffect, useCallback } from 'react';
import { Message, User, LoginNotification } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from './useNotifications';
import { apiService } from '../services/api';
import { audioManager } from '../utils/audioManager';
import { soundManager } from '../utils/soundManager';

interface MessagingHook {
  messages: Message[];
  unreadCounts: { [userId: number]: number };
  sendMessage: (receiverId: number, message: string) => Promise<void>;
  markAsRead: (messageId: number) => void;
  markAllAsRead: (senderId: number) => void;
  getConversation: (userId: number) => Message[];
  onlineUsers: User[];
  allUsers: User[];
  loginNotifications: LoginNotification[];
  dismissLoginNotification: (id: number) => void;
  loadMessages: () => Promise<void>;
}

// Mock data for messages
let mockMessages: Message[] = [];
let mockLoginNotifications: LoginNotification[] = [];
let messageIdCounter = 1;
let loginNotificationIdCounter = 1;


export const useMessaging = (): MessagingHook => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loginNotifications, setLoginNotifications] = useState<LoginNotification[]>(mockLoginNotifications);
  const [onlineUsers, setOnlineUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<{ [userId: number]: number }>({});
  const { user } = useAuth();

  // Load messages and unread counts
  const loadMessages = useCallback(async () => {
    try {
      const [messagesData, unreadData] = await Promise.all([
        apiService.getMessages().catch(error => {
          if (error.message.includes('Too many requests')) {
            console.warn('Rate limited while loading messages, skipping this request');
            return [];
          }
          throw error;
        }),
        apiService.getUnreadMessageCounts().catch(error => {
          if (error.message.includes('Too many requests')) {
            console.warn('Rate limited while loading unread counts, skipping this request');
            return {};
          }
          throw error;
        })
      ]);
      setMessages(messagesData);
      setUnreadCounts(unreadData);
    } catch (error) {
      if (!error.message.includes('Too many requests')) {
        console.error('Error loading messages:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadMessages();
      // Check for unread messages every 15 seconds
      const interval = setInterval(loadMessages, 15000); // 15 seconds
      return () => clearInterval(interval);
    }
  }, [user, loadMessages]);


  // Listen for user login events and new messages
  useEffect(() => {
    const handleUserLogin = (event: CustomEvent) => {
      const loginUser = event.detail;
      if (loginUser.id !== user?.id) {
        const notification: LoginNotification = {
          id: loginNotificationIdCounter++,
          user_id: loginUser.id,
          user: loginUser,
          login_time: new Date().toISOString(),
          ip_address: '192.168.1.100',
          user_agent: navigator.userAgent
        };
        
        mockLoginNotifications.push(notification);
        setLoginNotifications([...mockLoginNotifications]);
        
        // Show login notification
        showGlobalNotification.success(
          'Kullanıcı Girişi',
          `${loginUser.name} sisteme giriş yaptı`,
          { duration: 3000 }
        );
      }
    };

    const handleNewMessage = (event: CustomEvent) => {
      const { message, receiverId, senderId } = event.detail;
      console.log('🔔 New message event received:', { receiverId, senderId, currentUserId: user?.id });

      // Only show notification to the receiver
      if (user?.id === receiverId) {
        console.log('🔊 Playing notification sound for message');

        // Play notification sound with user interaction check
        const playSound = async () => {
          try {
            await soundManager.playMessageNotification();
            console.log('✅ Sound played successfully');
          } catch (error) {
            console.warn('⚠️ Sound play failed:', error);
            // Fallback to audioManager
            try {
              audioManager.playNotificationSound('message');
            } catch (fallbackError) {
              console.warn('⚠️ Fallback sound also failed:', fallbackError);
            }
          }
        };
        
        playSound();

        // Refresh messages for receiver immediately
        loadMessages();

        // Show toast notification
        showGlobalNotification.info(
          'Yeni Mesaj',
          `${message.sender_name}: ${message.message.substring(0, 50)}${message.message.length > 50 ? '...' : ''}`,
          { duration: 5000 }
        );
      } else {
        console.log('🚫 Not showing notification - wrong receiver');
      }
    };

    const handleRefreshMessages = () => {
      console.log('🔄 Refreshing messages...');
      loadMessages();
    };

    const handleMessageRead = (event: CustomEvent) => {
      const { messageId, readBy } = event.detail;
      console.log('📖 Message read event received:', { messageId, readBy });

      // Find the message and update unread count immediately
      const message = messages.find(m => m.id === messageId);
      if (message && message.receiver_id === readBy && !message.is_read) {
        console.log('📉 Updating unread count for sender:', message.sender_id);
        setUnreadCounts(prev => ({
          ...prev,
          [message.sender_id]: Math.max(0, (prev[message.sender_id] || 0) - 1)
        }));

        // Update the message as read in local state
        setMessages(prev => prev.map(m =>
          m.id === messageId ? { ...m, is_read: true, read_at: new Date().toISOString() } : m
        ));
      }
    };

    window.addEventListener('userLogin', handleUserLogin as EventListener);
    window.addEventListener('newMessage', handleNewMessage as EventListener);
    window.addEventListener('refreshMessages', handleRefreshMessages);
    window.addEventListener('messageRead', handleMessageRead as EventListener);
    
    return () => {
      window.removeEventListener('userLogin', handleUserLogin as EventListener);
      window.removeEventListener('newMessage', handleNewMessage as EventListener);
      window.removeEventListener('refreshMessages', handleRefreshMessages);
      window.removeEventListener('messageRead', handleMessageRead as EventListener);
    };
  }, [user, loadMessages]);

  const sendMessage = useCallback(async (receiverId: number, messageText: string): Promise<void> => {
    if (!user) return;

    try {
      const newMessage = await apiService.sendMessage(receiverId, messageText);
      
      // Refresh messages after sending
      await loadMessages();
      
      // Trigger message notification event for all instances
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('newMessage', { 
          detail: { 
            message: newMessage,
            receiverId: receiverId,
            senderId: user.id
          } 
        }));
        console.log('🔔 Message event dispatched:', { receiverId, senderId: user.id });
      }, 100);
      
      console.log(`Message sent from ${user.name} to receiver ${receiverId}: ${messageText}`);
    } catch (error) {
      console.error('Error sending message:', error);
      showGlobalNotification.error('Hata', 'Mesaj gönderilemedi');
    }
  }, [user, loadMessages]);

  const markAsRead = useCallback(async (messageId: number) => {
    try {
      // Find the message first for immediate update
      const message = messages.find(m => m.id === messageId);
      if (message && message.receiver_id === user?.id && !message.is_read) {
        // Update local state immediately
        setUnreadCounts(prev => ({
          ...prev,
          [message.sender_id]: Math.max(0, (prev[message.sender_id] || 0) - 1)
        }));

        setMessages(prev => prev.map(m =>
          m.id === messageId ? { ...m, is_read: true, read_at: new Date().toISOString() } : m
        ));
      }

      // Make API call (this will trigger SSE event for other clients)
      await apiService.markMessageAsRead(messageId);

      console.log('✅ Message marked as read:', messageId);
    } catch (error) {
      console.error('Error marking message as read:', error);
      // Revert local changes on error
      const message = messages.find(m => m.id === messageId);
      if (message && message.receiver_id === user?.id) {
        setUnreadCounts(prev => ({
          ...prev,
          [message.sender_id]: (prev[message.sender_id] || 0) + 1
        }));

        setMessages(prev => prev.map(m =>
          m.id === messageId ? { ...m, is_read: false, read_at: null } : m
        ));
      }
    }
  }, [messages, user]);

  const markAllAsRead = useCallback(async (senderId: number) => {
    try {
      // Immediately update local state (optimistic update) regardless of messages state
      setUnreadCounts(prev => ({
        ...prev,
        [senderId]: 0
      }));

      setMessages(prev => prev.map(m =>
        m.sender_id === senderId && m.receiver_id === user?.id && !m.is_read
          ? { ...m, is_read: true, read_at: new Date().toISOString() }
          : m
      ));

      // Single API call to mark all — does NOT depend on local messages state
      await apiService.markAllMessagesAsRead(senderId);

      console.log('✅ All messages marked as read for sender:', senderId);
    } catch (error) {
      console.error('Error marking all messages as read:', error);
      // Reload from server to get correct state
      loadMessages();
    }
  }, [user, loadMessages]);

  const getConversation = useCallback((userId: number): Message[] => {
    return messages.filter(message => 
      (message.sender_id === user?.id && message.receiver_id === userId) ||
      (message.sender_id === userId && message.receiver_id === user?.id)
    ).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }, [messages, user]);

  const dismissLoginNotification = useCallback((id: number) => {
    mockLoginNotifications = mockLoginNotifications.filter(n => n.id !== id);
    setLoginNotifications([...mockLoginNotifications]);
  }, []);

  // Update all users from API
  useEffect(() => {
    const loadAllUsers = async () => {
      try {
        // Sadece admin/superadmin kullanıcıları için users endpoint'ini çağır
        const [allUsersData, onlineUsersData] = await Promise.all([
          (['admin', 'superadmin'].includes(user?.role || '')) 
            ? apiService.getUsers().catch(() => [])
            : Promise.resolve([]),
          apiService.getOnlineUsers().catch(() => [])
        ]);
        
        const usersArray: User[] = Array.isArray(allUsersData) ? allUsersData : [];
        const onlineUsersArray: User[] = Array.isArray(onlineUsersData) ? onlineUsersData : [];

        const onlineUserIds = new Set(onlineUsersArray.map((u: User) => u.id));
        
        let allowedRoles: string[] = [];
        
        // Admin ve superadmin, diğer adminler, superadminler ve teknisyenlerle konuşabilir
        if (['admin', 'superadmin'].includes(user?.role || '')) {
          allowedRoles = ['admin', 'superadmin', 'teknisyen'];
        } else if (user?.role === 'teknisyen') {
          // Teknisyenler, diğer teknisyenler ve superadminlerle konuşabilir
          allowedRoles = ['teknisyen', 'superadmin'];
        } else {
          // Diğer roller sadece admin ve superadmin ile konuşabilir
          allowedRoles = ['admin', 'superadmin'];
        }
        
        const filteredUsers = usersArray
          .filter((u: User) => u.id !== user?.id && u.is_active && allowedRoles.includes(u.role))
          .map((u: User) => ({
            ...u,
            is_online: onlineUserIds.has(u.id)
          }))
          // Tüm kullanıcıları göster (offline olanlar da dahil);
        
        setAllUsers(filteredUsers);
        setOnlineUsers(filteredUsers.filter(u => u.is_online));
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };

    if (user) {
      loadAllUsers();
      // Increase interval to 60 seconds to reduce API calls
      const interval = setInterval(loadAllUsers, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  return {
    messages,
    unreadCounts,
    sendMessage,
    markAsRead,
    markAllAsRead,
    getConversation,
    onlineUsers,
    allUsers,
    loginNotifications,
    dismissLoginNotification,
    loadMessages
  };
};

// Global messaging hook instance
let globalMessagingHook: ReturnType<typeof useMessaging> | null = null;

export const setGlobalMessagingHook = (hook: ReturnType<typeof useMessaging>) => {
  globalMessagingHook = hook;
};

export const getGlobalMessagingHook = () => globalMessagingHook;
